import { useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover';
import { formatDate } from '@/utils/formatters';
import type { FundFigure, FundMetricsPoint } from '@/types/fund';
import {
  figureValue,
  formatFigure,
  formatFigureChange,
  quarterLabel,
} from './figureFormat';

interface FigureTileProps {
  figure: FundFigure;
  points: FundMetricsPoint[];
  currency: string;
}

function changeTone(change: number) {
  if (change > 0) return 'text-emerald-600';
  if (change < 0) return 'text-destructive';
  return 'text-muted-foreground';
}

function ChangeLine({ figure, points, currency }: FigureTileProps) {
  const latest = points.at(-1);
  const previous = points.at(-2);
  const value = latest ? figureValue(latest, figure.figure) : null;
  const previousValue = previous ? figureValue(previous, figure.figure) : null;

  const blank = <span className="text-xs text-muted-foreground">&nbsp;</span>;
  if (value === null || !previous) return blank;
  if (previousValue === null) {
    const reportedBefore = points
      .slice(0, -2)
      .some((point) => figureValue(point, figure.figure) !== null);
    if (reportedBefore) return blank;
    return (
      <span className="text-xs text-muted-foreground">
        First reported quarter
      </span>
    );
  }

  const change = value - previousValue;
  return (
    <span className={cn('text-xs tabular-nums', changeTone(change))}>
      {formatFigureChange(change, figure.format, currency)} vs{' '}
      {quarterLabel(previous.as_of)}
    </span>
  );
}

const CLOSE_DELAY_MS = 160;

function useHoverOpen() {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const show = () => {
    clearTimeout(closeTimerRef.current);
    setOpen(true);
  };
  const hide = () => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };
  return { open, show, hide };
}

export function FigureTile({ figure, points, currency }: FigureTileProps) {
  const latest = points.at(-1);
  const value = latest ? figureValue(latest, figure.figure) : null;
  const formatted = formatFigure(value, figure.format, currency);
  const { open, show, hide } = useHoverOpen();

  return (
    <Popover open={open}>
      <PopoverAnchor asChild>
        <div
          className="flex min-w-0 cursor-default flex-col gap-1.5 bg-card px-5 pb-4 pt-[18px] outline-none transition-colors hover:bg-muted/50 focus-visible:bg-muted/50"
          tabIndex={0}
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={show}
          onBlur={hide}
          data-testid={`figure-tile-${figure.figure}`}
        >
          <span className="text-[13px] font-medium text-muted-foreground">
            {figure.label}
          </span>
          <span className="self-start border-b-2 border-muted-foreground/40 text-2xl font-bold leading-tight tracking-tight tabular-nums">
            {formatted}
          </span>
          <ChangeLine figure={figure} points={points} currency={currency} />
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="flex w-80 flex-col gap-2.5 px-4 py-3.5"
        onMouseEnter={show}
        onMouseLeave={hide}
        onOpenAutoFocus={(event) => event.preventDefault()}
        data-testid={`figure-tile-${figure.figure}-detail`}
      >
        <span className="text-xs font-medium text-muted-foreground">
          {figure.label}
        </span>
        <span className="text-lg font-bold tracking-tight tabular-nums">
          {formatted}
        </span>
        <div className="flex flex-col gap-0.5 text-xs leading-relaxed text-muted-foreground">
          {figure.explanation && <span>{figure.explanation}</span>}
          {latest && (
            <span>
              As of {formatDate(latest.as_of)} · {quarterLabel(latest.as_of)}
            </span>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
